import React from "react";
import Button from "components/common/ui/buttons/Button/Button";
import BaseModal from "components/common/modals/Modal/Modal";
import { useSelector } from "react-redux";
import { getIdToken } from "services/auth/auth";
import styled from "styled-components";
import LoadingIndicator from "components/common/ui/LoadingIndicator/LoadingIndicator";
import { RootState } from "state/reducers";
import { shallowEqual } from "state/utilities";
import Dropdown from "../shared/Dropdown";
import Input from "../shared/Input";
import FileUpload from "../shared/FileUpload";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";

const ModalHeader = styled.h3`
    margin: 0;
    font-size: 24px;
    font-weight: 400;
`;

const ModalParagraph = styled.p`
    font-size: 16px;
    font-weight: 400;
    margin: 0 0 0.5rem 0;
`;

const ModalHeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
`;

const ModalCloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 28px;
    line-height: 1;
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 30px;
    height: 30px;

    &:before,
    &:after {
        position: absolute;
        left: 14px;
        top: 2.5px;
        content: " ";
        height: 25px;
        width: 2px;
        background-color: #333;
    }

    &:before {
        transform: rotate(45deg);
    }

    &:after {
        transform: rotate(-45deg);
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const ModalFooterContainer = styled.div`
    margin-top: 1rem;
`;

const RequestError = styled.p`
    color: red;
    margin: 0 0 1.5rem 0;
`;

interface ModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
}

const mapState = (state: RootState) => ({
    token: getIdToken(state),
    selectedOrganizationId: state.common.user.selectedOrganizationId,
});

const Modal: React.FC<ModalProps> = ({ isOpen, onRequestClose }) => {
    const { token, selectedOrganizationId } = useSelector(mapState, shallowEqual);
    const translate = useTranslate();

    const priorityOptions = [
        { value: "Matala", label: translate("featureRequests.featureRequestsModal.priorityLow") },
        { value: "Keski", label: translate("featureRequests.featureRequestsModal.priorityMedium") },
        { value: "Korkea", label: translate("featureRequests.featureRequestsModal.priorityHigh") },
    ];

    const [customerTitle, setCustomerTitle] = React.useState("");
    const [customerDescription, setCustomerDescription] = React.useState("");
    const [datahubPriority, setDatahubPriority] = React.useState("");
    const [customerBenefits, setCustomerBenefits] = React.useState("");
    const [customerAdditionalInfo, setCustomerAdditionalInfo] = React.useState("");
    const [attachments, setAttachments] = React.useState<File[]>([]);

    const [requestError, setRequestError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);

    const body = {
        customerTitle: customerTitle.trim(),
        customerDescription: customerDescription.trim(),
        datahubPriority: datahubPriority.trim(),
        customerBenefits: customerBenefits.trim(),
        customerAdditionalInfo: customerAdditionalInfo.trim(),
        selectedOrganizationId,
    };

    const isValid =
        body.customerTitle &&
        body.customerDescription &&
        body.datahubPriority &&
        body.customerBenefits;

    React.useEffect(() => {
        if (isOpen) {
            setCustomerTitle("");
            setCustomerDescription("");
            setDatahubPriority("");
            setCustomerBenefits("");
            setCustomerAdditionalInfo("");
            setAttachments([]);
            setRequestError(null);
        }
    }, [isOpen]);

    const handleSubmit = () => {
        setLoading(true);
        setRequestError(null);

        const formData = new FormData();

        formData.append("customerTitle", body.customerTitle);
        formData.append("customerDescription", body.customerDescription);
        formData.append("datahubPriority", body.datahubPriority);
        formData.append("customerBenefits", body.customerBenefits);
        formData.append("customerAdditionalInfo", body.customerAdditionalInfo);
        formData.append("selectedOrganizationId", selectedOrganizationId || "");

        attachments.forEach((file) => {
            formData.append("attachments", file);
        });

        fetch("/api/feature-requests", {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Request failed: ${response.statusText}`);
                } else {
                    onRequestClose();
                }
            })
            .catch((error) => setRequestError(error.message))
            .finally(() => setLoading(false));
    };

    return (
        <BaseModal isOpen={isOpen} onRequestClose={onRequestClose} disableModalScrolling={true}>
            <ModalHeaderContainer>
                <ModalHeader>
                    {translate("featureRequests.featureRequestsModal.newFeatureRequest")}
                </ModalHeader>
                <ModalCloseButton
                    onClick={onRequestClose}
                    disabled={loading}
                    aria-label={translate("featureRequests.featureRequestsModal.closeWindow")}
                />
            </ModalHeaderContainer>
            <ModalParagraph>
                {translate("featureRequests.featureRequestsModal.requiredFieldsNotice")}
            </ModalParagraph>
            <Input
                label={translate("featureRequests.featureRequestsModal.title")}
                required={true}
                value={customerTitle}
                onChange={setCustomerTitle}
                disabled={loading}
            />
            <Input
                label={translate("featureRequests.featureRequestsModal.shortDescription")}
                required={true}
                value={customerDescription}
                onChange={setCustomerDescription}
                multiline={true}
                disabled={loading}
            />
            <Dropdown
                label={translate("featureRequests.featureRequestsModal.priority")}
                options={priorityOptions}
                selectedValue={datahubPriority}
                onValueChange={setDatahubPriority}
                placeholder={translate("featureRequests.featureRequestsModal.selectPriority")}
                disabled={loading}
                fullWidth={true}
            />
            <Input
                label={translate("featureRequests.featureRequestsModal.benefits")}
                required={true}
                value={customerBenefits}
                onChange={setCustomerBenefits}
                multiline={true}
                disabled={loading}
            />
            <Input
                label={translate("featureRequests.featureRequestsModal.additionalInfo")}
                required={false}
                value={customerAdditionalInfo}
                onChange={setCustomerAdditionalInfo}
                multiline={true}
                disabled={loading}
            />
            <FileUpload
                label={translate("featureRequests.featureRequestsModal.attachments")}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                disabled={loading}
            />
            <ModalFooterContainer>
                {requestError && <RequestError>{requestError}</RequestError>}
                <Button onClick={handleSubmit} disabled={!isValid || loading}>
                    {translate("featureRequests.featureRequestsModal.send")}
                </Button>
            </ModalFooterContainer>
            <LoadingIndicator isLoading={loading} />
        </BaseModal>
    );
};

export default Modal;
